import { Button } from '@neo/primitives/button'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { Network } from 'lucide-react'
import type { ReactNode } from 'react'
import type { RExternalApp, RExternalAppDetails } from 'v2/types/api/settings'
import { INTEGRATION_AUTH_TYPES } from '../utils/constants'

interface IntegrationAppSelectionProps {
  externalApps: RExternalApp
  searchTerm: string
  showCustomMcp: boolean
  onAppClick: (app: RExternalAppDetails) => void
  onCustomMcpClick: () => void
}

interface CatalogCardProps {
  icon: ReactNode
  title: string
  subtitle: string
  tooltip: string
  onClick: () => void
  testId: string
}

/** One tile in the catalog grid. Shared so the custom-server card sits flush with the apps. */
const CatalogCard = ({ icon, title, subtitle, tooltip, onClick, testId }: CatalogCardProps) => (
  <Button
    variant="outline"
    className="flex h-28 w-full min-w-0 flex-col justify-between rounded-md border border-st2 px-4 font-inter normal-case tracking-normal shadow-none hover:bg-hv1 hover:text-foreground hover:after:opacity-0 focus-visible:border-brand focus-visible:bg-hv1 focus-visible:text-foreground"
    onClick={onClick}
    data-testid={testId}
  >
    <div className="flex min-w-0 flex-1 flex-col items-center justify-center space-y-1">
      <Tooltip content={tooltip}>
        <span className="flex h-8 w-8 items-center justify-center">{icon}</span>
      </Tooltip>
      <Typography
        variant="P/400"
        className="line-clamp-2 flex h-10 w-full items-center justify-center whitespace-normal break-words text-center leading-5 text-primary"
      >
        {title}
      </Typography>
      <Typography variant="XS/400" className="text-center text-secondary">
        {subtitle}
      </Typography>
    </div>
  </Button>
)

interface AppCardProps {
  app: RExternalAppDetails
  onClick: () => void
}

const AppCard = ({ app, onClick }: AppCardProps) => (
  <CatalogCard
    icon={<img src={app.logo_url} alt={app.name} className="h-8 w-8 object-contain" />}
    title={app.name}
    subtitle={INTEGRATION_AUTH_TYPES[app.oauth_type] ?? 'Other'}
    tooltip={`${app.name} integration`}
    onClick={onClick}
    testId={`button-app-${app.name.toLowerCase().replace(/\s/g, '-')}`}
  />
)

export const IntegrationAppSelection = ({
  externalApps,
  searchTerm,
  showCustomMcp,
  onAppClick,
  onCustomMcpClick,
}: IntegrationAppSelectionProps) => {
  const normalizedSearch = searchTerm.toLowerCase()
  // One walk of the entries: build the matching list and drop empty categories in
  // the same step. Previously this was map -> filter, which allocated a full
  // intermediate array of categories just to throw the empty ones away
  // (react-doctor/js-combine-iterations).
  const categories = Object.entries(externalApps).flatMap(([name, apps]) => {
    const matching = ((apps ?? []) as RExternalAppDetails[]).filter((app) =>
      app.name.toLowerCase().includes(normalizedSearch),
    )
    return matching.length > 0 ? [{ name, apps: matching }] : []
  })

  return (
    <ScrollArea className="h-full w-full">
      <div className="mt-4 space-y-6 pb-4 pr-4">
        {showCustomMcp && (
          <div>
            <Typography variant="XS/500" className="mb-3 text-secondary">
              Custom
            </Typography>
            <div className="grid grid-cols-3 gap-4">
              <CatalogCard
                icon={<Network className="h-7 w-7 text-secondary" strokeWidth={1.5} />}
                title="MCP Server"
                subtitle="Remote"
                tooltip="Connect a remote MCP server"
                onClick={onCustomMcpClick}
                testId="button-add-custom-mcp"
              />
            </div>
          </div>
        )}
        {categories.map((category) => (
          <div key={category.name}>
            <Typography variant="XS/500" className="mb-3 text-secondary">
              {category.name}
            </Typography>
            <div className="grid grid-cols-3 gap-4">
              {category.apps.map((app) => (
                <AppCard key={app.id} app={app} onClick={() => onAppClick(app)} />
              ))}
            </div>
          </div>
        ))}
        {categories.length === 0 && searchTerm && (
          <p
            className="py-8 text-center text-s-400 text-secondary"
            data-testid="text-no-apps-found"
          >
            No apps or tools match &ldquo;{searchTerm}&rdquo;.
          </p>
        )}
      </div>
    </ScrollArea>
  )
}
